import {Button} from '@hm/fundamental'
import {Close} from '@mui/icons-material'
import {Box, Typography, IconButton} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import ReviewIllustration from '../../../../../Assets/orderChangeResult/review.svg'
import SuccessIllustration from '../../../../../Assets/orderChangeResult/success.svg'
import {OrderChangeResultType} from '../../types/orderChangeResult.types'

interface OrderChangeResultProps {
  /** The result type to display */
  resultType: OrderChangeResultType
  /** Callback when user clicks "View order details" */
  onViewOrderDetails: () => void
  /** Callback when user clicks close button */
  onClose?: () => void
  /** Job site info to display in header */
  jobSiteInfo?: string
}

/**
 * OrderChangeResult Component
 *
 * Displays the result of an order change submission.
 * Two variants:
 * - success: Order change was approved immediately
 * - review: Order change needs dispatch review/approval
 */
export const OrderChangeResult: React.FC<OrderChangeResultProps> = ({
  resultType,
  onViewOrderDetails,
  onClose,
  jobSiteInfo
}) => {
  const {t} = useTranslation()
  const isSuccess = resultType === 'success'

  const headerTitle = isSuccess
    ? t('orderIntake.rmc.orderManagement.result.headerApproved', 'Order change approved')
    : t('orderIntake.rmc.orderManagement.result.headerSubmitted', 'Order change submitted')

  const mainTitle = isSuccess
    ? t('orderIntake.rmc.orderManagement.result.titleApproved', 'Your order change was approved')
    : t(
        'orderIntake.rmc.orderManagement.result.titleSubmitted',
        'We are reviewing your order changes'
      )

  const description = isSuccess
    ? t(
        'orderIntake.rmc.orderManagement.result.descriptionApproved',
        'Your order will be updated in just a few seconds.'
      )
    : t(
        'orderIntake.rmc.orderManagement.result.descriptionSubmitted',
        'Please check back later to see the status of your request.'
      )

  return (
    <Box p={3} height="100%">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px'
        }}
        data-test-id="order-change-result"
      >
        {/* Header */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 600,
                lineHeight: '24px',
                color: '#29333d',
                fontFamily: 'Inter'
              }}
            >
              {headerTitle}
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{
                padding: '4px',
                color: '#52667a'
              }}
              data-test-id="close-result-button"
            >
              <Close sx={{fontSize: '16px'}} />
            </IconButton>
          </Box>
          {jobSiteInfo && (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '16px',
                color: '#52667a',
                fontFamily: 'Inter'
              }}
            >
              {jobSiteInfo}
            </Typography>
          )}
        </Box>

        {/* Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
            paddingY: '32px'
          }}
        >
          {isSuccess ? (
            <img src={SuccessIllustration} alt="Success" style={{width: 200, height: 200}} />
          ) : (
            <img src={ReviewIllustration} alt="Under review" style={{width: 200, height: 200}} />
          )}

          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              lineHeight: '32px',
              color: '#29333d',
              fontFamily: 'Inter',
              textAlign: 'center'
            }}
          >
            {mainTitle}
          </Typography>

          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              color: '#52667a',
              fontFamily: 'Inter',
              textAlign: 'center',
              maxWidth: 300
            }}
          >
            {description}
          </Typography>
        </Box>

        {/* Action Button */}
        <Button
          variant="primary"
          size="large"
          onClick={onViewOrderDetails}
          className="w-full"
          data-test-id="view-order-details-button"
        >
          {t('orderIntake.rmc.orderManagement.result.viewOrderDetails', 'View order details')}
        </Button>
      </Box>
    </Box>
  )
}
